import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  PLP_PAGE_TYPE,
  PLP_TAB_BANNER_TYPES
} from '../product-list-page-constants';

import {
  useDataLayer,
  useMyStore,
  useProductEntryDataLayerValues,
  useTreadwellPreferences
} from '../../../shared/hooks/hooks';

import {
  getPdlDataLayerValues,
  getProductCoreStatus,
  getProductImpressions
} from '../../../shared/utils/product/product';
import { sessionStorage } from '../../../shared/utils/storage/storage';
import { isArrayWithLength } from '../../../shared/utils/validators/validators';

import {
  FILTERS_APPLIED,
  FILTERS_APPLIED_COUNT,
  FILTERS_PATH,
  PAGE_CATEGORY,
  PAGE_NAME,
  PAGE_SECTION_NAME,
  PAGE_SUBCATEGORY,
  PAGE_TYPE,
  PRODUCT_LIST,
  PRODUCT_LIST_CORE_STATUS,
  PRODUCT_LIST_RELATIVE_POSITION,
  PRODUCT_LIST_SORT,
  TOTAL_NUMBER_OF_PAGES,
  TOTAL_NUMBER_OF_RESULTS,
  TREADWELL_DRIVERTYPE_OPTION_WINTER
} from '../../../shared/constants/data-layer';
import { PRODUCT_TYPES } from '../../../shared/constants/products';
import { SORT_PARAMS } from '../../../shared/constants/search-types';
import { PLP_PRODUCT_SEARCH_ID } from '../../../shared/constants/storage-keys';

export default function usePlpAnalytics(
  products,
  productType,
  selectedFilters,
  currentSort,
  isLoading,
  isPrebuilt,
  pageNum,
  numberOfPages,
  category,
  selectedProductBannerTab
) {
  const [shouldSendAnalytics, setShouldSendAnalytics] = useState(false);
  const { hash, search: queryParams } = useLocation();
  const { myStore, nearbyStorePreferences } = useMyStore();
  const serializedStorePreferences = JSON.stringify(nearbyStorePreferences);
  const { isTreadwellFlow, treadwellPreferences } = useTreadwellPreferences();
  const serializedTreadwellPreferences = JSON.stringify(treadwellPreferences);
  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();
  const { updateDataLayer } = useDataLayer({
    shouldResetPageOnFitmentChange: true
  });

  const prevHashRef = useRef(hash);
  const prevQueryParamsRef = useRef(queryParams);
  const prevSerializedStorePreferencesRef = useRef(serializedStorePreferences);
  const prevSerializedTreadwellPreferencesRef = useRef(
    serializedTreadwellPreferences
  );

  const isTreadwellGetStarted =
    selectedProductBannerTab === PLP_TAB_BANNER_TYPES.TREADWELL &&
    !isTreadwellFlow;
  const isTreadwellGetStartedPrevRef = useRef(isTreadwellGetStarted);
  const hasToggledTreadwellGetStartedTab =
    isTreadwellGetStarted !== isTreadwellGetStartedPrevRef.current;
  const showTreadwellWinterTiresPrevRef = useRef(
    treadwellPreferences?.showWinterTires
  );
  const hasToggledShowTreadwellWinterTires =
    isTreadwellFlow &&
    treadwellPreferences?.showWinterTires !==
      showTreadwellWinterTiresPrevRef.current;

  const categoryName = category?.name;
  let pageName = '';

  if (productType === PRODUCT_TYPES.TIRES) {
    pageName = 'tire_list';
  } else if (productType === PRODUCT_TYPES.WHEELS) {
    pageName = 'wheel_list';
  } else if (productType === PRODUCT_TYPES.ACCESSORIES) {
    pageName = 'accessory_list';
  } else if (isPrebuilt) {
    pageName = 'prebuilt_list';
  }

  const getDataLayerValues = useCallback(
    products => {
      const filtersPath =
        queryParams === prevQueryParamsRef.current ? 'Entry' : 'Apply Filter';
      const isTreadwellRecommended =
        isTreadwellFlow &&
        currentSort?.toLowerCase() === SORT_PARAMS.RECOMMENDED;
      const defaultAppliedFilters = isTreadwellRecommended ? ['Treadwell'] : [];
      let appliedFilters = defaultAppliedFilters;
      let appliedFiltersCount = '0';
      let pageSubcategory = 'product search results';

      if (isPrebuilt) {
        pageSubcategory = 'prebuilt product list';
      }

      if (isArrayWithLength(selectedFilters)) {
        appliedFilters = selectedFilters.map(f => `${f.valueName}:`);
        appliedFiltersCount = selectedFilters.length.toString();
      }
      if (categoryName) {
        appliedFilters.unshift(`${categoryName}`);
      }

      const searchIdString = `${filtersPath}_${appliedFilters}_${currentSort}`;
      sessionStorage(PLP_PRODUCT_SEARCH_ID, searchIdString);

      const productEntryDataLayerValues = products?.map(
        getProductEntryDataLayerValues
      );
      const page = pageNum + 1;

      let treadwellValues = {};
      if (isTreadwellRecommended) {
        treadwellValues = {
          ...getPdlDataLayerValues(products, treadwellPreferences),
          [TREADWELL_DRIVERTYPE_OPTION_WINTER]:
            treadwellPreferences?.showWinterTires ? 'y' : 'n'
        };
      }

      if (isTreadwellGetStarted) {
        return {
          [PAGE_CATEGORY]: 'plp landing',
          [PAGE_NAME]: 'treadwell plp landing',
          [PAGE_TYPE]: 'treadwell_plp'
        };
      }

      const productListDetails = products?.reduce(
        (coreStatus, product) => {
          coreStatus[PRODUCT_LIST_CORE_STATUS].push(
            getProductCoreStatus(product)
          );
          coreStatus[PRODUCT_LIST_RELATIVE_POSITION].push(
            String(coreStatus[PRODUCT_LIST_RELATIVE_POSITION].length + 1)
          );
          if (product?.rearProduct) {
            coreStatus[PRODUCT_LIST_CORE_STATUS].push(
              coreStatus[PRODUCT_LIST_CORE_STATUS][
                coreStatus[PRODUCT_LIST_CORE_STATUS].length - 1
              ]
            );
            coreStatus[PRODUCT_LIST_RELATIVE_POSITION].push(
              String(coreStatus[PRODUCT_LIST_RELATIVE_POSITION].length + 1)
            );
          }
          return coreStatus;
        },
        {
          [PRODUCT_LIST_CORE_STATUS]: [],
          [PRODUCT_LIST_RELATIVE_POSITION]: []
        }
      );

      return {
        [FILTERS_APPLIED]: appliedFilters,
        [FILTERS_APPLIED_COUNT]: appliedFiltersCount,
        [FILTERS_PATH]: filtersPath,
        [PAGE_CATEGORY]: 'product search',
        [PAGE_NAME]: pageName,
        [PAGE_SECTION_NAME]: document.title,
        [PAGE_SUBCATEGORY]: pageSubcategory,
        [PAGE_TYPE]: PLP_PAGE_TYPE,
        [PRODUCT_LIST]: searchIdString,
        [PRODUCT_LIST_CORE_STATUS]:
          productListDetails?.[PRODUCT_LIST_CORE_STATUS],
        [PRODUCT_LIST_RELATIVE_POSITION]:
          productListDetails?.[PRODUCT_LIST_RELATIVE_POSITION],
        [PRODUCT_LIST_SORT]: currentSort?.toLowerCase(),
        [TOTAL_NUMBER_OF_PAGES]: numberOfPages,
        [TOTAL_NUMBER_OF_RESULTS]: page,
        ...getProductImpressions(productEntryDataLayerValues, searchIdString),
        ...treadwellValues,
        'store_addl_selected_distances': nearbyStorePreferences.map(storeCode =>
          myStore?.nearByStores
            ?.find(s => s.code === storeCode)
            ?.distance?.toString()
        ),
        'store_addl_selected_num': nearbyStorePreferences.length,
        'store_addl_selected_store_code': nearbyStorePreferences
      };
    },
    [
      isTreadwellFlow,
      isTreadwellGetStarted,
      isPrebuilt,
      selectedFilters,
      treadwellPreferences,
      pageName,
      pageNum,
      myStore,
      nearbyStorePreferences,
      numberOfPages,
      currentSort,
      queryParams,
      categoryName,
      getProductEntryDataLayerValues
    ]
  );

  const updatePlpAnalytics = useCallback(
    (products, shouldCreateNewEntry = true) => {
      updateDataLayer(getDataLayerValues(products), shouldCreateNewEntry);
      prevQueryParamsRef.current = queryParams;
    },
    [getDataLayerValues, queryParams, updateDataLayer]
  );

  useEffect(() => {
    if (isLoading || hasToggledTreadwellGetStartedTab) {
      setShouldSendAnalytics(true);
    }
  }, [isLoading, hasToggledTreadwellGetStartedTab]);

  useEffect(() => {
    if (hash === '' && prevHashRef.current === '#/wheel-visualizer') {
      setShouldSendAnalytics(true);
    } else {
      prevHashRef.current = hash;
    }
  }, [hash]);

  useEffect(() => {
    if (isLoading === false && shouldSendAnalytics) {
      let shouldCreateNewDataLayerEntry = true;

      if (hasToggledTreadwellGetStartedTab) {
        shouldCreateNewDataLayerEntry = false;
        isTreadwellGetStartedPrevRef.current = isTreadwellGetStarted;
      } else if (
        serializedStorePreferences !== prevSerializedStorePreferencesRef.current
      ) {
        shouldCreateNewDataLayerEntry = false;
        prevSerializedStorePreferencesRef.current = serializedStorePreferences;
      } else if (hash === '' && prevHashRef.current === '#/wheel-visualizer') {
        shouldCreateNewDataLayerEntry = false;
        prevHashRef.current = hash;
      } else if (
        serializedTreadwellPreferences !==
        prevSerializedTreadwellPreferencesRef.current
      ) {
        shouldCreateNewDataLayerEntry = false;
        prevSerializedTreadwellPreferencesRef.current =
          serializedTreadwellPreferences;
      }
      setShouldSendAnalytics(false);
      updatePlpAnalytics(products, shouldCreateNewDataLayerEntry);
    }
  }, [
    hash,
    isLoading,
    isTreadwellGetStarted,
    serializedStorePreferences,
    shouldSendAnalytics,
    updatePlpAnalytics,
    products,
    hasToggledShowTreadwellWinterTires,
    serializedTreadwellPreferences,
    hasToggledTreadwellGetStartedTab
  ]);

  return { updatePlpAnalytics };
}
